import { NavLink } from 'react-router-dom'
import { useFloatingOffset } from '@/shared/hooks/use-floating-offset'
import { useNav } from '@/shared/hooks/use-nav'
import { CalendarCheck, CalendarDays, MapPin, Plus, User } from 'lucide-react'
import { clsx } from 'clsx'

// Primary navigation — bottom tab bar (Apple HIG / Material best practice for
// 3–5 destinations; beats a hamburger for discovery + thumb reach). "Crear" is
// an emphasized center ACTION that opens the full-screen create flow, not a tab
// pane (FAB-over-content is deprecated in favour of bar-integrated actions).
//
// ⚠️ SIZES ARE ARBITRARY VALUES ON PURPOSE. @upadelmatch/tokens remaps Tailwind's
// numeric spacing scale onto the brand ladder (`--spacing-1..7 → --space-1..7`),
// so `h-5`=24px, `h-6`=32px, `h-7`=48px — while 8+ falls through to Tailwind's
// own scale, making `h-8`=32px SMALLER than `h-7`. This bar shipped with the
// active pill written `h-7 w-12` intending a 28×48 stadium; it rendered a 48×48
// circle, pixel-identical to the Crear button beside it, so the active tab read
// as a second (dimmed) Crear. Measured 2026-08-07 at 390×844. Explicit px here
// is immune to the remap and states the intent — do not "tidy" it back to scale
// classes without re-measuring.
const TABS = [
  { to: '/partidos', label: 'Partidos', icon: CalendarDays },
  { to: '/lugares', label: 'Lugares', icon: MapPin },
  // "Agenda", not "Mis": the only label that wasn't a whole word (19px wide
  // against Partidos' 46px, and an abbreviation among four real words). The
  // screen holds Próximos AND Pasados, which "Agenda" covers and "Mis partidos"
  // can't — that measures ~78px against a 76px cell, so it would truncate or
  // force the type back down. Trophy → CalendarCheck for the same reason: a
  // trophy implies rankings, and this is a schedule.
  { to: '/mis-partidos', label: 'Agenda', icon: CalendarCheck },
  { to: '/perfil', label: 'Perfil', icon: User },
] as const

export function BottomTabBar() {
  // Publishes its height so the update banner + toasts float ABOVE it.
  const setBottomChrome = useFloatingOffset()
  const navigate = useNav()
  return (
    <nav ref={setBottomChrome} className="chrome-foot safe-bottom shrink-0 border-t border-[var(--chrome-line)] bg-[var(--chrome-bg)]">
      {/* One 48px icon row shared by all five slots: Crear's circle fills it,
          tab indicators sit centered inside it. Labels then align structurally
          instead of by the arithmetic coincidence a negative margin produced. */}
      {/* No pb-* here: `.safe-bottom` is deliberately unlayered and always wins,
          supplying max(0.75rem, env(safe-area-inset-bottom)). A padding utility
          beside it is dead code. Height budget: 1 border + 4 pt + 48 row + 4 gap
          + 16 label + 12 safe = 85px, i.e. no regression on the 86px it replaces. */}
      <div className="grid grid-cols-5 px-1 pt-1">
        <Tab {...TABS[0]} />
        <Tab {...TABS[1]} />
        {/* center create action — the ONLY filled accent element in the bar */}
        <button
          type="button"
          aria-label="Crear partido"
          onClick={() => navigate('/crear')}
          className="flex flex-col items-center gap-1"
        >
          <span className="h-[48px] flex items-center justify-center">
            <span className="h-[48px] w-[48px] inline-flex items-center justify-center rounded-full bg-[var(--accent)] text-[color:var(--accent-fg)] shadow-[var(--shadow-popover)] active:scale-95 transition-transform">
              <Plus size={24} />
            </span>
          </span>
          <span className="text-[length:var(--fs-xs)] font-semibold leading-tight text-[color:var(--fg-muted)]">
            Crear
          </span>
        </button>
        <Tab {...TABS[2]} />
        <Tab {...TABS[3]} />
      </div>
    </nav>
  )
}

function Tab({ to, label, icon: Icon }: { to: string; label: string; icon: typeof CalendarDays }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'flex flex-col items-center gap-1',
          isActive ? 'text-[color:var(--accent)]' : 'text-[color:var(--fg-muted)]',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className="h-[48px] flex items-center justify-center">
            {/* Active indicator — a 32×56 STADIUM, deliberately a different shape
                class from Crear's 48px circle. Colour is never the only cue
                (a11y), and the shape difference is what stops the active tab
                rhyming with the action button. */}
            <span
              className={clsx(
                'inline-flex items-center justify-center h-[32px] w-[56px] rounded-full transition-colors',
                isActive && 'bg-[var(--accent-soft)]',
              )}
            >
              <Icon size={22} />
            </span>
          </span>
          <span className="text-[length:var(--fs-xs)] font-semibold leading-tight">{label}</span>
        </>
      )}
    </NavLink>
  )
}
